import { useEffect, useMemo, useRef } from 'react';
import { onStageMessage, postStage } from './lessonStage';
import { lyricsMessage, songNameForFile } from './stageModel';

/**
 * Drives the second screen's scrolling words from the page's ONE track player
 * (2026-09-27). AUTOMATIC, per Anthony: when a track with timed words starts
 * playing, the stage switches to its lyrics (even over a video); then every
 * position change (~4 a second from the audio element, plus pause and seek)
 * goes out as a clock tick. A stage opened mid-song says "hello" and gets the
 * current words and position at once. With no stage open, the messages go
 * nowhere, which is harmless.
 *
 * THE TEACHER'S CHOICE WINS (2026-09-28). The words are sent whenever the
 * track's words are known, paused or not, so the menu's "Lyrics" always has
 * something to show; but they carry `show: true` (take the screen) only the
 * first time a given track starts playing. Resuming it, or the page simply
 * re-rendering, never overrides a Blank or a Video the teacher picked.
 * Anthony, on the build it replaces: "the buttons for choosing what to
 * display don't seem to work" (they did, for a second, then this hook
 * switched the screen back).
 *
 * Returns { hasLyrics }: the playing track has timed words to show.
 */
const useStageLyrics = ({ player, files = [], docs = [] }) => {
	const current = player && player.current;
	const key = current && current.engine === 'audio' ? current.key : null;
	const file = key ? current.file : null;
	const title = useMemo(() => songNameForFile(docs, file), [docs, file]);
	const base = useMemo(() => lyricsMessage({ key, file, files, title }), [key, file, files, title]);
	const isPlaying = Boolean(player && player.isPlaying);
	const t = (player && player.currentTime) || 0;

	// The latest state for the "hello" reply, without re-subscribing.
	const latest = useRef({ base, t, isPlaying });
	latest.current = { base, t, isPlaying };

	// The track whose words last took the screen; only a DIFFERENT track
	// starting to play takes it again.
	const shownKey = useRef(null);

	// Words: always sent; `show` only when a new track starts playing.
	useEffect(() => {
		if (!base) return;
		const show = isPlaying && shownKey.current !== base.key;
		if (show) shownKey.current = base.key;
		postStage({ ...base, t: latest.current.t, playing: isPlaying, show });
	}, [base, isPlaying]);

	// Clock: position, pause, seek.
	useEffect(() => {
		if (!base) return;
		postStage({ type: 'clock', key: base.key, t, playing: isPlaying });
	}, [base, t, isPlaying]);

	// A stage opened mid-song asks where we are.
	useEffect(
		() =>
			onStageMessage((m) => {
				const { base: b, t: now, isPlaying: playing } = latest.current;
				// A fresh stage shows the words at once if the track is playing.
				if (m.type === 'hello' && b) postStage({ ...b, t: now, playing, show: playing });
			}),
		[]
	);

	return { hasLyrics: Boolean(base) };
};

export default useStageLyrics;
